/**
 * The active language, for the whole app.
 *
 * Precedence, which is the whole design in three lines:
 *
 *   inside a plan  -> the plan's own language (it prints the PDF too)
 *   everywhere else -> the stored preference
 *   first visit     -> the best match for the browser's languages
 *
 * Creating a plan stamps the current preference onto it; changing the language
 * inside the editor writes both, so the next plan inherits the choice.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import {
  type Catalogue,
  type Direction,
  type LocaleCode,
  directionOf,
  isLocale,
  resolveLocale,
} from './index'

const STORAGE_KEY = 'yerbeyer.locale'

interface I18nValue {
  t: Catalogue
  locale: LocaleCode
  direction: Direction
  setLocale: (locale: LocaleCode) => void
}

const I18nContext = createContext<I18nValue | null>(null)

/** The preference this browser last used, if it is still a language we ship. */
export function storedLocale(): LocaleCode | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved && isLocale(saved) ? saved : null
  } catch {
    return null // private mode: fall back to the browser's languages
  }
}

export function rememberLocale(locale: LocaleCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* non-fatal: the choice simply won't survive a reload */
  }
}

/** The language to start in, before any plan has been opened. */
export function preferredLocale(): LocaleCode {
  return storedLocale() ?? resolveLocale(navigator.languages ?? [navigator.language])
}

interface Props {
  locale: LocaleCode
  catalogue: Catalogue
  onLocaleChange: (locale: LocaleCode) => void
  children: ReactNode
}

export function I18nProvider({ locale, catalogue, onLocaleChange, children }: Props) {
  const direction = directionOf(locale)

  // The document element, not a wrapper div: `dir` has to be on <html> for
  // scrollbars, form controls and the browser's own UI to follow it.
  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = direction
  }, [locale, direction])

  const value = useMemo<I18nValue>(
    () => ({ t: catalogue, locale, direction, setLocale: onLocaleChange }),
    [catalogue, locale, direction, onLocaleChange],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useT must be used inside <I18nProvider>')
  return value
}

/**
 * The active catalogue.
 *
 *     const t = useT()
 *     <input aria-label={t.topbar.planTitle} />
 *     <span>{t.dashboard.tables(4)}</span>
 *
 * Property access rather than `t('some.key')`: autocomplete works, a typo is a
 * compile error, and there is no lookup to get wrong at runtime.
 */
export function useT(): Catalogue {
  return useI18n().t
}

export function useLocale(): Omit<I18nValue, 't'> {
  const { t: _t, ...rest } = useI18n()
  return rest
}

/**
 * Loads a catalogue for the given locale, keeping the previous one on screen
 * until the new one has arrived so switching never flashes an empty layout.
 */
export function useCatalogue(locale: LocaleCode, initial: Catalogue) {
  const [catalogue, setCatalogue] = useState(initial)

  useEffect(() => {
    let cancelled = false
    void import('./index').then(({ loadCatalogue }) =>
      loadCatalogue(locale).then((loaded) => {
        if (!cancelled) setCatalogue(loaded)
      }),
    )
    return () => {
      cancelled = true
    }
  }, [locale])

  return catalogue
}

/**
 * `Intl.DateTimeFormat.format` throws on Invalid Date, and a throw mid-render
 * takes the whole page with it — so a value this browser cannot read is shown
 * as it was written instead.
 */
function formatOrRaw(format: Intl.DateTimeFormat, value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return Number.isNaN(date.getTime()) ? String(value) : format.format(date)
}

/** Date formatting that follows the active language rather than the OS. */
export function useDateFormat() {
  const { locale } = useLocale()
  return useCallback(
    (value: string | Date) =>
      formatOrRaw(
        new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
        value,
      ),
    [locale],
  )
}

/** A number of bytes as the reader writes sizes: "740 kB", "3.2 MB", "٣٫٢ م.ب". */
export function useSizeFormat() {
  const { locale } = useLocale()
  return useCallback(
    (bytes: number) => {
      const kilobytes = bytes / 1024
      const megabytes = kilobytes / 1024
      const [value, unit, digits] =
        megabytes < 1
          ? [Math.max(1, Math.round(kilobytes)), 'kilobyte', 0]
          : megabytes < 1024
            ? [megabytes, 'megabyte', 1]
            : [megabytes / 1024, 'gigabyte', 1]
      return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: digits,
      }).format(value)
    },
    [locale],
  )
}

/** Date and time, for "last edited" moments that can fall on the same day. */
export function useDateTimeFormat() {
  const { locale } = useLocale()
  return useCallback(
    (value: string | Date) =>
      formatOrRaw(
        new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        value,
      ),
    [locale],
  )
}
